<script>
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Banner from 'dashboard/components/ui/Banner.vue';
import UsagesAPI from 'dashboard/api/usages';

const TOKEN_WARNING_BALANCE = 500000;
const TOKEN_CRITICAL_BALANCE = 200000;
const STORAGE_WARNING_PERCENTAGE = 80;
const STORAGE_CRITICAL_PERCENTAGE = 95;

const EMPTY_USAGE = {
  pilot_tokens: {},
  storage: {},
};

export default {
  components: { Banner },
  setup() {
    const { accountId } = useAccount();
    const { isAdmin } = useAdmin();

    return {
      accountId,
      isAdmin,
    };
  },
  data() {
    return {
      usage: EMPTY_USAGE,
      isFetching: false,
    };
  },
  computed: {
    activeAlerts() {
      const alerts = [this.tokenAlert, this.storageAlert].filter(Boolean);
      return alerts.sort((a, b) => b.priority - a.priority);
    },
    activeAlert() {
      const alerts = this.activeAlerts;
      if (!alerts.length) return null;

      const highestPriorityAlert = alerts[0];
      return {
        colorScheme: highestPriorityAlert.colorScheme,
        message: alerts.map(alert => alert.message).join('；'),
      };
    },
    tokenAlert() {
      const tokenUsage = this.usage?.pilot_tokens || {};
      const total = Number(tokenUsage.total) || 0;
      if (!total) return null;

      const consumed = Number(tokenUsage.consumed) || 0;
      const balance = Math.max(total - consumed, 0);

      if (balance <= TOKEN_CRITICAL_BALANCE) {
        return {
          priority: 40,
          colorScheme: 'alert',
          message: `Token 余额不足 ${this.formatNumber(balance)}，请及时购买 Token`,
        };
      }

      if (balance <= TOKEN_WARNING_BALANCE) {
        return {
          priority: 20,
          colorScheme: 'warning',
          message: `Token 余额偏低，当前剩余 ${this.formatNumber(balance)}`,
        };
      }

      return null;
    },
    storageAlert() {
      const storageUsage = this.usage?.storage || {};
      const total = Number(storageUsage.total) || 0;
      if (!total) return null;

      const percentage = Number(storageUsage.percentage) || 0;

      if (percentage >= STORAGE_CRITICAL_PERCENTAGE) {
        return {
          priority: 30,
          colorScheme: 'alert',
          message: `存储空间已使用 ${percentage}%，请及时扩容`,
        };
      }

      if (percentage >= STORAGE_WARNING_PERCENTAGE) {
        return {
          priority: 10,
          colorScheme: 'warning',
          message: `存储空间已使用 ${percentage}%，建议关注剩余容量`,
        };
      }

      return null;
    },
    shouldShowBanner() {
      return this.isAdmin && !this.isFetching && !!this.activeAlert;
    },
  },
  watch: {
    accountId: {
      immediate: true,
      handler() {
        this.fetchUsage();
      },
    },
    isAdmin() {
      this.fetchUsage();
    },
  },
  methods: {
    async fetchUsage() {
      if (!this.accountId || !this.isAdmin) return;

      this.isFetching = true;
      try {
        const response = await UsagesAPI.getUsage();
        this.usage = response.data || EMPTY_USAGE;
      } catch {
        this.usage = EMPTY_USAGE;
      } finally {
        this.isFetching = false;
      }
    },
    routeToUsage() {
      this.$router.push({
        path: `/app/accounts/${this.accountId}/subscription/usage`,
      });
    },
    formatNumber(value) {
      return Number(value).toLocaleString('zh-CN');
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="shouldShowBanner"
    :color-scheme="activeAlert.colorScheme"
    :banner-message="activeAlert.message"
    action-button-label="查看用量"
    has-action-button
    @primary-action="routeToUsage"
  />
</template>
